import * as React from 'react';
import {
  Table,
  TableHeader,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
  makeStyles,
  tokens,
  Button,
  Spinner,
  Text,
} from '@fluentui/react-components';
import { Edit20Regular, Delete20Regular } from '@fluentui/react-icons';
import { Marker } from '../../../types/marker';

const useStyles = makeStyles({
  container: {
    width: '100%',
  },
  table: {
    // Tabela visível apenas em desktop
    '@media (max-width: 768px)': {
      display: 'none',
    },
  },
  mobileCards: {
    display: 'none',
    '@media (max-width: 768px)': {
      display: 'flex',
      flexDirection: 'column',
      gap: tokens.spacingVerticalM,
    },
  },
  card: {
    padding: tokens.spacingHorizontalL,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: tokens.spacingVerticalS,
    gap: tokens.spacingHorizontalS,
  },
  cardContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  cardDescricao: {
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground1,
  },
  cardActions: {
    display: 'flex',
    gap: tokens.spacingHorizontalXS,
    flexShrink: 0,
  },
  actions: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    padding: tokens.spacingVerticalXXL,
  },
  error: {
    color: tokens.colorPaletteRedForeground1,
    textAlign: 'center',
    padding: tokens.spacingVerticalL,
  },
  actionsCell: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
  },
  // Estilos para controlar largura das colunas
  idCell: {
    width: '15%',
    minWidth: '80px',
  },
  descricaoCell: {
    width: '75%',
    minWidth: '200px',
  },
  actionsHeaderCell: {
    width: '10%',
    minWidth: '120px',
  },
});

interface MarkerGridProps {
  markers: Marker[];
  onEdit: (marker: Marker) => void;
  onDelete: (id: number) => void;
  loading?: boolean;
  error?: string | null;
}

const MarkerGrid: React.FC<MarkerGridProps> = ({
  markers,
  onEdit,
  onDelete,
  loading,
  error,
}) => {
  const styles = useStyles();

  if (loading) {
    return (
      <div className={styles.loading}>
        <Spinner size="large" label="Carregando marcadores..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.error}>
        <Text>Erro ao carregar marcadores: {error}</Text>
      </div>
    );
  }

  const columns = [
    { key: 'id', label: 'ID' },
    { key: 'descricao', label: 'Descrição' },
    { key: 'acoes', label: 'Ações' },
  ];

  // Função helper para mapear chave da coluna para classe CSS
  const getColumnClass = (key: string) => {
    switch (key) {
      case 'id': return styles.idCell;
      case 'descricao': return styles.descricaoCell;
      case 'acoes': return styles.actionsHeaderCell;
      default: return '';
    }
  };

  // Função helper para renderizar conteúdo da célula
  const renderCellContent = (marker: Marker, key: string) => {
    switch (key) {
      case 'id':
        return marker.id;
      case 'descricao':
        return marker.descricao;
      case 'acoes':
        return (
          <div className={styles.actionsCell}>
            <Button
              appearance="subtle"
              icon={<Edit20Regular />}
              onClick={() => onEdit(marker)}
              size="small"
            >
              Editar
            </Button>
            <Button
              appearance="subtle"
              icon={<Delete20Regular />}
              onClick={() => onDelete(marker.id)}
              size="small"
            >
              Excluir
            </Button>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className={styles.container}>
      {/* Tabela para desktop */}
      <Table className={styles.table}>
        <TableHeader>
          <TableRow>
            {columns.map((column) => (
              <TableHeaderCell key={column.key} className={getColumnClass(column.key)}>
                {column.label}
              </TableHeaderCell>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {markers.map((marker) => (
            <TableRow key={marker.id}>
              {columns.map((column) => (
                <TableCell key={column.key} className={getColumnClass(column.key)}>
                  {renderCellContent(marker, column.key)}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {/* Cards para mobile */}
      <div className={styles.mobileCards}>
        {markers.map((marker) => (
          <div key={marker.id} className={styles.card}>
            <div className={styles.cardHeader}>
              <div className={styles.cardContent}>
                <Text className={styles.cardDescricao}>{marker.descricao}</Text>
              </div>
              <div className={styles.cardActions}>
                <Button
                  appearance="subtle"
                  icon={<Edit20Regular />}
                  onClick={() => onEdit(marker)}
                  size="small"
                >
                  Editar
                </Button>
                <Button
                  appearance="subtle"
                  icon={<Delete20Regular />}
                  onClick={() => onDelete(marker.id)}
                  size="small"
                >
                  Excluir
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default MarkerGrid;